import React, { useState, useEffect } from 'react';
import { SidebarProvider } from '../../components/navigation/Sidebar';
import Sidebar from '../../components/navigation/Sidebar';
import QuickActions from '../../components/navigation/QuickActions';
import DateRangeSelector from '../../components/navigation/DateRangeSelector';
import MetricCard from './components/MetricCard';
import PerformanceChart from './components/PerformanceChart';
import TopPinsGrid from './components/TopPinsGrid';
import TrendingKeywords from './components/TrendingKeywords';
import BoardSelector from './components/BoardSelector';
import PinPerformanceTable from './components/PinPerformanceTable';
import AutomationStatus from './components/AutomationStatus';
import Icon from '../../components/AppIcon';
import Select from '../../components/ui/Select';

const PinterestAnalyticsHub = () => {
  const [selectedBoard, setSelectedBoard] = useState('all');
  const [selectedCampaign, setSelectedCampaign] = useState('all');
  const [lastSync, setLastSync] = useState(new Date());

  useEffect(() => {
    const syncInterval = setInterval(() => {
      setLastSync(new Date());
    }, 900000);

    return () => clearInterval(syncInterval);
  }, []);

  const boards = [
  { id: 'board1', name: 'Home Decor Ideas', pinCount: 245 },
  { id: 'board2', name: 'Fitness & Wellness', pinCount: 189 },
  { id: 'board3', name: 'Recipe Collection', pinCount: 312 },
  { id: 'board4', name: 'Travel Inspiration', pinCount: 156 }];


  const campaignOptions = [
  { value: 'all', label: 'All Campaigns' },
  { value: 'spring2025', label: 'Spring Collection 2025' },
  { value: 'holiday', label: 'Holiday Special' },
  { value: 'summer', label: 'Summer Vibes' }];


  const kpiMetrics = [
  {
    title: 'Total Impressions',
    value: '2.4M',
    change: '+18.2%',
    changeType: 'positive',
    icon: 'Eye',
    trend: [12, 18, 15, 22, 28, 25, 32, 30, 35, 38, 42, 45]
  },
  {
    title: 'Pin Saves',
    value: '45.2K',
    change: '+24.5%',
    changeType: 'positive',
    icon: 'Bookmark',
    trend: [8, 12, 15, 18, 22, 25, 28, 32, 35, 38, 42, 45]
  },
  {
    title: 'Click-Through Rate',
    value: '3.8%',
    change: '+0.4%',
    changeType: 'positive',
    icon: 'MousePointerClick',
    trend: [15, 18, 20, 22, 25, 28, 30, 32, 35, 38, 40, 42]
  },
  {
    title: 'Top Board Performance',
    value: '156K',
    change: '+12.3%',
    changeType: 'positive',
    icon: 'LayoutGrid',
    trend: [20, 22, 25, 28, 30, 32, 35, 38, 40, 42, 44, 45]
  }];


  const performanceData = [
  { date: '11/19', impressions: 185000, engagement: 7200 },
  { date: '11/20', impressions: 198000, engagement: 8100 },
  { date: '11/21', impressions: 212000, engagement: 8900 },
  { date: '11/22', impressions: 195000, engagement: 7800 },
  { date: '11/23', impressions: 225000, engagement: 9500 },
  { date: '11/24', impressions: 242000, engagement: 10200 },
  { date: '11/25', impressions: 268000, engagement: 11500 },
  { date: '11/26', impressions: 285000, engagement: 12800 }];


  const topPins = [
  {
    id: 1,
    title: 'Modern Minimalist Living Room Design with Natural Light',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_133e9e66d-1764727504518.png",
    imageAlt: 'Bright modern minimalist living room with white walls, large windows, natural light streaming in, beige sofa, wooden coffee table, and green plants',
    impressions: '45.2K',
    saves: '2.8K',
    clicks: '1.2K',
    engagementRate: '8.5'
  },
  {
    id: 2,
    title: 'Healthy Breakfast Bowl Recipe with Fresh Fruits',
    image: "https://images.unsplash.com/photo-1728078465803-ffe80c87b1e6",
    imageAlt: 'Colorful healthy breakfast bowl with fresh strawberries, blueberries, banana slices, granola, yogurt, and mint leaves on white marble surface',
    impressions: '38.5K',
    saves: '2.4K',
    clicks: '980',
    engagementRate: '7.8'
  },
  {
    id: 3,
    title: 'Yoga Meditation Pose at Sunset Beach',
    image: "https://images.unsplash.com/photo-1542688827815-ec9d9013ae22",
    imageAlt: 'Woman in black athletic wear performing yoga meditation pose on sandy beach during golden hour sunset with ocean waves in background',
    impressions: '42.1K',
    saves: '2.6K',
    clicks: '1.1K',
    engagementRate: '8.2'
  },
  {
    id: 4,
    title: 'Tropical Paradise Beach Vacation Destination',
    image: "https://images.unsplash.com/photo-1709474613637-3f53d9510f7c",
    imageAlt: 'Stunning tropical beach with crystal clear turquoise water, white sand, palm trees, and blue sky with fluffy white clouds',
    impressions: '51.3K',
    saves: '3.2K',
    clicks: '1.5K',
    engagementRate: '9.1'
  }];


  const trendingKeywords = [
  { term: 'home decor 2025', searchVolume: '125K', change: '+45%', trend: 'up', pins: '2.4K', trending: true },
  { term: 'healthy recipes', searchVolume: '98K', change: '+32%', trend: 'up', pins: '1.8K', trending: true },
  { term: 'fitness motivation', searchVolume: '87K', change: '+28%', trend: 'up', pins: '1.5K', trending: false },
  { term: 'travel destinations', searchVolume: '76K', change: '+18%', trend: 'up', pins: '1.2K', trending: false },
  { term: 'diy projects', searchVolume: '65K', change: '-5%', trend: 'down', pins: '980', trending: false }];


  const detailedPins = [
  {
    id: 1,
    title: 'Modern Minimalist Living Room Design',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_133e9e66d-1764727504518.png",
    imageAlt: 'Bright modern minimalist living room with white walls, large windows, natural light streaming in, beige sofa, wooden coffee table, and green plants',
    impressions: 45200,
    saves: 2800,
    clicks: 1200,
    engagementRate: 8.5,
    board: 'Home Decor Ideas',
    createdDate: '11/20/2025'
  },
  {
    id: 2,
    title: 'Healthy Breakfast Bowl Recipe',
    image: "https://images.unsplash.com/photo-1728078465803-ffe80c87b1e6",
    imageAlt: 'Colorful healthy breakfast bowl with fresh strawberries, blueberries, banana slices, granola, yogurt, and mint leaves on white marble surface',
    impressions: 38500,
    saves: 2400,
    clicks: 980,
    engagementRate: 7.8,
    board: 'Recipe Collection',
    createdDate: '11/21/2025'
  },
  {
    id: 3,
    title: 'Yoga Meditation Pose at Sunset',
    image: "https://images.unsplash.com/photo-1542688827815-ec9d9013ae22",
    imageAlt: 'Woman in black athletic wear performing yoga meditation pose on sandy beach during golden hour sunset with ocean waves in background',
    impressions: 42100,
    saves: 2600,
    clicks: 1100,
    engagementRate: 8.2,
    board: 'Fitness & Wellness',
    createdDate: '11/22/2025'
  },
  {
    id: 4,
    title: 'Tropical Paradise Beach Vacation',
    image: "https://images.unsplash.com/photo-1709474613637-3f53d9510f7c",
    imageAlt: 'Stunning tropical beach with crystal clear turquoise water, white sand, palm trees, and blue sky with fluffy white clouds',
    impressions: 51300,
    saves: 3200,
    clicks: 1500,
    engagementRate: 9.1,
    board: 'Travel Inspiration',
    createdDate: '11/23/2025'
  },
  {
    id: 5,
    title: 'Cozy Reading Nook Interior Design',
    image: "https://images.unsplash.com/photo-1568990416308-c998af4cf9ee",
    imageAlt: 'Cozy reading corner with comfortable armchair, floor lamp, wooden bookshelf filled with books, soft throw blanket, and window with natural light',
    impressions: 36800,
    saves: 2200,
    clicks: 890,
    engagementRate: 7.4,
    board: 'Home Decor Ideas',
    createdDate: '11/24/2025'
  },
  {
    id: 6,
    title: 'Mediterranean Diet Meal Prep',
    image: "https://images.unsplash.com/photo-1587443622544-d2e939eef49b",
    imageAlt: 'Mediterranean diet meal prep containers with grilled chicken, quinoa, cherry tomatoes, cucumbers, olives, and feta cheese on wooden table',
    impressions: 39200,
    saves: 2500,
    clicks: 1050,
    engagementRate: 8.0,
    board: 'Recipe Collection',
    createdDate: '11/25/2025'
  },
  {
    id: 7,
    title: 'Home Workout Routine Equipment',
    image: "https://images.unsplash.com/photo-1588733162458-28da22719e51",
    imageAlt: 'Home workout equipment setup with yoga mat, dumbbells, resistance bands, water bottle, and towel on wooden floor with natural lighting',
    impressions: 44500,
    saves: 2700,
    clicks: 1180,
    engagementRate: 8.3,
    board: 'Fitness & Wellness',
    createdDate: '11/26/2025'
  },
  {
    id: 8,
    title: 'Mountain Hiking Trail Adventure',
    image: "https://images.unsplash.com/photo-1658541161387-3e50de281c48",
    imageAlt: 'Scenic mountain hiking trail with winding path through green forest, rocky terrain, and snow-capped peaks visible in distance under blue sky',
    impressions: 47800,
    saves: 2900,
    clicks: 1320,
    engagementRate: 8.7,
    board: 'Travel Inspiration',
    createdDate: '11/26/2025'
  },
  {
    id: 9,
    title: 'Scandinavian Kitchen Design Ideas',
    image: "https://images.unsplash.com/photo-1653427601675-6ed383e20f0c",
    imageAlt: 'Modern Scandinavian kitchen with white cabinets, wooden countertops, minimalist design, pendant lights, and open shelving with plants',
    impressions: 41200,
    saves: 2550,
    clicks: 1080,
    engagementRate: 7.9,
    board: 'Home Decor Ideas',
    createdDate: '11/26/2025'
  },
  {
    id: 10,
    title: 'Vegan Smoothie Bowl Recipe',
    image: "https://images.unsplash.com/photo-1610450620997-6921021865da",
    imageAlt: 'Vibrant vegan smoothie bowl with purple acai base topped with fresh berries, coconut flakes, chia seeds, and sliced kiwi in white bowl',
    impressions: 35600,
    saves: 2100,
    clicks: 850,
    engagementRate: 7.2,
    board: 'Recipe Collection',
    createdDate: '11/26/2025'
  }];


  const queueData = {
    completed: 156,
    pending: 23,
    failed: 4,
    successRate: 95.6
  };

  const formatLastSync = () => {
    const now = new Date();
    const diff = Math.floor((now - lastSync) / 60000);
    if (diff < 1) return 'Just now';
    if (diff < 60) return `${diff} min ago`;
    return `${Math.floor(diff / 60)} hr ago`;
  };

  return (
    <SidebarProvider>
      <div className="min-h-screen bg-background">
        <Sidebar />
        
        <div className="main-content">
          <div className="mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
              <div>
                <h1 className="text-3xl font-bold text-foreground mb-2">
                  Pinterest Analytics Hub
                </h1>
                <p className="text-muted-foreground">
                  Comprehensive Pinterest marketing performance insights and automation monitoring
                </p>
              </div>
              
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 px-4 py-2 bg-card border border-border rounded-lg">
                  <Icon name="RefreshCw" size={16} className="text-success" />
                  <span className="text-sm text-muted-foreground">
                    Last sync: {formatLastSync()}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex flex-col lg:flex-row lg:items-center gap-4 mb-6">
              <BoardSelector
                boards={boards}
                selectedBoard={selectedBoard}
                onBoardChange={setSelectedBoard} />

              
              <div className="flex items-center gap-3">
                <Icon name="Filter" size={20} className="text-muted-foreground" />
                <Select
                  options={campaignOptions}
                  value={selectedCampaign}
                  onChange={setSelectedCampaign}
                  placeholder="Select campaign"
                  className="w-64" />

              </div>

              <DateRangeSelector />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {kpiMetrics?.map((metric, index) =>
            <MetricCard key={index} {...metric} />
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8">
            <div className="lg:col-span-8">
              <PerformanceChart
                data={performanceData}
                title="Pin Performance Over Time" />

            </div>
            
            <div className="lg:col-span-4 space-y-6">
              <TopPinsGrid pins={topPins} />
              <TrendingKeywords keywords={trendingKeywords} />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8">
            <div className="lg:col-span-9">
              <PinPerformanceTable pins={detailedPins} />
            </div>
            
            <div className="lg:col-span-3">
              <AutomationStatus queueData={queueData} />
            </div>
          </div>
        </div>

        <QuickActions />
      </div>
    </SidebarProvider>);

};

export default PinterestAnalyticsHub;